import React, { useId } from 'react';
import { EApiLanguageCode } from '@datatype/iyagigo/api-type';

export type TFlagSize = 'small' | 'medium' | 'large';

interface IProps {
  language: EApiLanguageCode;
  size?: TFlagSize;
}

// 국기 원본 비율: 미국 19:10, 일본 3:2. 나란히 놓았을 때 높이를 맞추기 위해 3:2로 통일한다.
const ASPECT_RATIO = 3 / 2;

// IconBlock의 small/medium/large(20/24/35)에 맞춘 가로 폭.
const FLAG_WIDTHS: Record<TFlagSize, number> = {
  small: 20,
  medium: 25,
  large: 35,
};

const VIEW_BOX_WIDTH = 30;
const VIEW_BOX_HEIGHT = 20;

const US_RED = '#b22234';
const US_BLUE = '#3c3b6e';
const JP_RED = '#bc002d';
const WHITE = '#ffffff';
const BORDER = 'rgba(0, 0, 0, 0.22)';
const BORDER_WIDTH = 0.9;

// 미국기 13줄무늬. 홀수 인덱스(흰색)는 바탕으로 처리하고 빨간 줄만 그린다.
const US_STRIPE_HEIGHT = VIEW_BOX_HEIGHT / 13;
const US_RED_STRIPE_INDEXES = [0, 2, 4, 6, 8, 10, 12];

// 칸톤(파란 사각형)은 위 7줄, 가로 40% 를 차지한다.
const US_CANTON_WIDTH = VIEW_BOX_WIDTH * 0.4;
const US_CANTON_HEIGHT = US_STRIPE_HEIGHT * 7;

// 실제 성조기는 9행 × (6,5) 교대 배치의 50개 별.
// 칩 크기(18px)에서 별 하나는 1px 미만이라 원으로 그리되, 배치 비율은 원본을 따른다.
const US_STAR_ROWS = 9;
const US_STAR_EVEN_ROW_COLUMNS = 6;
const US_STAR_RADIUS = 0.3;

const createUsStars = () => {
  const stars: React.ReactNode[] = [];
  // 별 그리드는 칸톤 안쪽에 여백을 두고 배치된다.
  const gapX = US_CANTON_WIDTH / (US_STAR_EVEN_ROW_COLUMNS * 2);
  const gapY = US_CANTON_HEIGHT / (US_STAR_ROWS + 1);

  for (let row = 0; row < US_STAR_ROWS; row += 1) {
    const isOddRow = row % 2 === 1;
    const columns = isOddRow ? US_STAR_EVEN_ROW_COLUMNS - 1 : US_STAR_EVEN_ROW_COLUMNS;
    for (let column = 0; column < columns; column += 1) {
      const cx = gapX * (column * 2 + 1) + (isOddRow ? gapX : 0);
      const cy = gapY * (row + 1);
      stars.push(<circle key={`${row}-${column}`} cx={cx} cy={cy} r={US_STAR_RADIUS} fill={WHITE} />);
    }
  }
  return stars;
};

const Flag: React.FC<IProps> = ({ language, size = 'medium' }) => {
  const clipId = `flag-clip-${useId().replace(/:/g, '')}`;
  const width = FLAG_WIDTHS[size];
  const height = width / ASPECT_RATIO;

  const UsStripes = US_RED_STRIPE_INDEXES.map((index) => (
    <rect
      key={index}
      x={0}
      y={US_STRIPE_HEIGHT * index}
      width={VIEW_BOX_WIDTH}
      height={US_STRIPE_HEIGHT}
      fill={US_RED}
    />
  ));

  const UsStars = createUsStars();

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${VIEW_BOX_WIDTH} ${VIEW_BOX_HEIGHT}`}
      xmlns='http://www.w3.org/2000/svg'
      role='img'
      aria-label={language === EApiLanguageCode.ENGLISH ? '미국 국기' : '일본 국기'}
      style={{ display: 'block', flex: 'none' }}>
      <defs>
        <clipPath id={clipId}>
          <rect x={0} y={0} width={VIEW_BOX_WIDTH} height={VIEW_BOX_HEIGHT} rx={2} ry={2} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        <rect x={0} y={0} width={VIEW_BOX_WIDTH} height={VIEW_BOX_HEIGHT} fill={WHITE} />
        {language === EApiLanguageCode.ENGLISH ? (
          <>
            {UsStripes}
            <rect x={0} y={0} width={US_CANTON_WIDTH} height={US_CANTON_HEIGHT} fill={US_BLUE} />
            {UsStars}
          </>
        ) : (
          <circle cx={VIEW_BOX_WIDTH / 2} cy={VIEW_BOX_HEIGHT / 2} r={VIEW_BOX_HEIGHT * 0.3} fill={JP_RED} />
        )}
      </g>
      {/* 흰 배경 위에서 국기 경계가 사라지지 않도록 테두리를 덧그린다.
          일본기는 흰 바탕이 대부분이라 테두리가 약하면 붉은 원만 떠 보인다. */}
      <rect
        x={BORDER_WIDTH / 2}
        y={BORDER_WIDTH / 2}
        width={VIEW_BOX_WIDTH - BORDER_WIDTH}
        height={VIEW_BOX_HEIGHT - BORDER_WIDTH}
        rx={2}
        ry={2}
        fill='none'
        stroke={BORDER}
        strokeWidth={BORDER_WIDTH}
      />
    </svg>
  );
};

export default Flag;
